import Reveal from "@/components/Reveal";
import { Ship, Droplets, Radar, Fish, Wheat, CloudRain, Leaf, Gauge, ArrowUpRight } from "lucide-react";

const OCEAN = {
  id: "oceans",
  overline: "02. Ocean Intelligence",
  title: "Guarding 7,500 km of coastline.",
  desc: "From the Arabian Sea to the Bay of Bengal, we watch every wake. Detect dark vessels, illegal fishing and oil spills the moment they appear.",
  image: "https://images.unsplash.com/photo-1446776877081-d282a0f896e2?auto=format&fit=crop&w=1400&q=80",
  glow: "#00E5FF",
  features: [
    { icon: Ship, t: "Vessel & dark-ship detection" },
    { icon: Fish, t: "Illegal fishing alerts" },
    { icon: Droplets, t: "Oil spill signatures" },
    { icon: Radar, t: "Coastal erosion & change" },
  ],
};

const AGRI = {
  id: "agriculture",
  overline: "03. Agri Intelligence",
  title: "Yield foresight, field by field.",
  desc: "Millions of hectares, resolved to the plot. Track crop health, soil moisture and drought stress, and forecast yields a season ahead.",
  image: "https://images.unsplash.com/photo-1508175688576-0c076b47b5b5?auto=format&fit=crop&w=1400&q=80",
  glow: "#10B981",
  features: [
    { icon: Leaf, t: "Crop health (NDVI)" },
    { icon: Gauge, t: "Soil moisture index" },
    { icon: CloudRain, t: "Drought & flood risk" },
    { icon: Wheat, t: "Yield forecasting" },
  ],
};

function SolutionBlock({ data, flip }) {
  return (
    <div id={data.id} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
      <Reveal className={flip ? "lg:order-2" : ""}>
        <div className="group relative overflow-hidden rounded-2xl border border-white/10">
          <div className="absolute inset-0 z-10 bg-black/50" />
          <div
            className="absolute inset-x-0 top-0 z-20 h-24 opacity-70"
            style={{ background: `linear-gradient(180deg, ${data.glow}22, transparent)` }}
          />
          <img
            src={data.image}
            alt={data.title}
            className="h-[420px] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          />
          {/* scan line */}
          <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden">
            <div
              className="absolute inset-x-0 h-24 animate-scan"
              style={{ background: `linear-gradient(180deg, transparent, ${data.glow}33, transparent)` }}
            />
          </div>
        </div>
      </Reveal>

      <div className={flip ? "lg:order-1" : ""}>
        <Reveal>
          <p className="overline" style={{ color: data.glow }}>{data.overline}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
            {data.title}
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">{data.desc}</p>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {data.features.map((f, i) => (
            <Reveal key={f.t} delay={0.25 + i * 0.07}>
              <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-arjuna-surface/40 px-4 py-3.5 transition-colors duration-300 hover:border-white/25">
                <f.icon className="h-5 w-5 shrink-0" style={{ color: data.glow }} />
                <span className="text-sm text-slate-200">{f.t}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.5}>
          <a href="#demo" className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: data.glow }}>
            See it in a live demo
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </Reveal>
      </div>
    </div>
  );
}

export default function Solutions() {
  return (
    <section className="relative bg-arjuna-ink py-24 sm:py-32" data-testid="solutions">
      <div className="container-x space-y-28">
        <SolutionBlock data={OCEAN} flip={false} />
        <SolutionBlock data={AGRI} flip={true} />
      </div>
    </section>
  );
}
